import { SubmissionStatus, SubmissionStepType } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useWasteStepper } from "hooks/useWasteStepper";
import { Navigate } from "react-router-dom";
import { WASTE_SUBMISSION_STEP_PRESENTATION } from "util/wasteSubmissionStepMetadata";

import { StepperOverview } from "./StepperOverview";

/**
 * The stepper's index (empty-slug) route. Flows with an Overview step (the
 * 8700-12) land on it; flows without one (document forms) redirect to their
 * first step, so the index is never a dead end. The shell renders skeletons
 * until the submission resolves, so `stepperSteps` is always present here.
 *
 * In review/signing, document forms land on Review & sign; a reviewer who
 * hasn't acknowledged the Understand step lands there first.
 */
export function StepperIndexRoute() {
  const { submission, canEdit } = useWasteStepper();
  const { user } = useCurrentUser();
  const steps = (submission?.stepperSteps ?? []).map(({ step }) => step);

  if (steps.includes(SubmissionStepType.Overview)) {
    return <StepperOverview />;
  }

  const inReviewOrSigning =
    submission?.status === SubmissionStatus.EncampInitialReview ||
    submission?.status === SubmissionStatus.InReview;
  if (inReviewOrSigning && steps.includes(SubmissionStepType.ReviewAndSign)) {
    const myReviewer = (submission?.reviewers ?? []).find((r) => r.user.id === user?.id);
    const needsToAcknowledge = !canEdit && !!myReviewer && !myReviewer.acknowledgedAt;
    const landingStep =
      needsToAcknowledge && steps.includes(SubmissionStepType.Understand)
        ? SubmissionStepType.Understand
        : SubmissionStepType.ReviewAndSign;
    return <Navigate to={WASTE_SUBMISSION_STEP_PRESENTATION[landingStep].routeSlug} replace />;
  }

  const firstStep = steps[0];
  if (!firstStep) {
    return null;
  }
  return <Navigate to={WASTE_SUBMISSION_STEP_PRESENTATION[firstStep].routeSlug} replace />;
}
